import { useLoaderData, Link, type LoaderFunctionArgs } from 'react-router-dom';
import { loadModelBundle, type ModelPageBundle } from '../../data/loaders';
import { ComponentBreakdown } from '../../components/ComponentBreakdown/ComponentBreakdown';
import { SentimentDistributionBar } from '../../components/SentimentDistribution/SentimentDistribution';
import { SourceMix } from '../../components/SourceMix/SourceMix';
import {
  formatCI,
  formatLargeInt,
  formatN,
  formatPostDate,
  formatRank,
  formatScore,
  formatWeekLabel,
} from '../../lib/format';
import { usePageMeta } from '../../lib/use-page-meta';
import styles from './ModelPage.module.css';

export async function modelLoader({ params }: LoaderFunctionArgs): Promise<ModelPageBundle> {
  const id = params.id;
  if (!id) throw new Response('Missing model id', { status: 400 });
  return loadModelBundle(id);
}

function buildSummary(bundle: ModelPageBundle): string {
  const { model } = bundle;
  const { stats, components, sample_size, vibe_score, vibe_score_ci } = model;
  const total = stats.distributions.overall_sentiment;
  const all = total.strongly_negative + total.negative + total.neutral + total.positive + total.strongly_positive + total.mixed;
  const positiveShare = all > 0 ? (total.positive + total.strongly_positive) / all : 0;
  const negativeShare = all > 0 ? (total.negative + total.strongly_negative) / all : 0;

  const rankLine = model.rank
    ? `${model.display_name} ranks ${ordinal(model.rank)} this week with a reception score of ${formatScore(vibe_score)} ${formatCI(vibe_score_ci)} from ${formatLargeInt(sample_size)} high-confidence tweets.`
    : `${model.display_name} is tracked this week but does not yet have enough public discussion to rank with confidence. Its observed reception score is ${formatScore(vibe_score)} ${formatCI(vibe_score_ci)} from ${formatLargeInt(sample_size)} high-confidence tweets.`;

  const toneLine =
    positiveShare > negativeShare + 0.15
      ? `The tone of public discussion is broadly positive — recommendations and approving usage notes outnumber complaints.`
      : negativeShare > positiveShare + 0.15
      ? `The tone of public discussion leans negative — complaints and dissatisfaction outweigh recommendations.`
      : `The tone of public discussion is mixed, with positive and negative reactions roughly in balance.`;

  const componentLine = chooseComponentLine(components.scores);

  return `${rankLine} ${toneLine} ${componentLine}`;
}

function chooseComponentLine(scores: { quality: number; reliability: number; price_value: number; usage_intent: number }): string {
  const items: Array<{ label: string; value: number }> = [
    { label: 'quality', value: scores.quality },
    { label: 'reliability', value: scores.reliability },
    { label: 'cost perception', value: scores.price_value },
    { label: 'sustained usage intent', value: scores.usage_intent },
  ];
  items.sort((a, b) => b.value - a.value);
  const top = items[0];
  const bottom = items[items.length - 1];
  if (Math.abs(top.value - bottom.value) < 4) {
    return `Component scores are close to flat across quality, reliability, cost perception and usage intent — the family has no obvious strong or weak axis this week.`;
  }
  return `On the component scores, ${top.label} (${formatScore(top.value)}) is the strongest signal and ${bottom.label} (${formatScore(bottom.value)}) is the softest.`;
}

function ordinal(n: number): string {
  const j = n % 10, k = n % 100;
  if (j === 1 && k !== 11) return `${n}st`;
  if (j === 2 && k !== 12) return `${n}nd`;
  if (j === 3 && k !== 13) return `${n}rd`;
  return `${n}th`;
}

export function ModelPage() {
  const bundle = useLoaderData() as ModelPageBundle;
  const { model, meta } = bundle;
  const isRanked = model.coverage_status === 'ranked';
  const summary = buildSummary(bundle);

  usePageMeta({
    title: isRanked
      ? `${model.display_name} · #${model.rank} on ViBE · Issue ${String(meta.week).padStart(2, '0')}`
      : `${model.display_name} · tracked on ViBE · Issue ${String(meta.week).padStart(2, '0')}`,
    description: isRanked
      ? `${model.display_name} (${model.provider}) ranks #${model.rank} this week with a reception score of ${model.vibe_score.toFixed(1)} from ${model.sample_size.toLocaleString('en-US')} high-confidence tweets.`
      : `${model.display_name} (${model.provider}) is tracked this week but does not yet have enough public discussion to rank with confidence.`,
  });

  return (
    <article className={styles.page}>
      {/* ─── Breadcrumb ─────────────────────────── */}
      <nav className={`${styles.breadcrumb} container-wide`} aria-label="Breadcrumb">
        <Link to="/" className={styles.breadcrumbLink}>← All families</Link>
        <span className={styles.breadcrumbDivider}>·</span>
        <span className={styles.breadcrumbCurrent}>{formatWeekLabel(meta.week, meta.week_slug)}</span>
      </nav>

      {/* ─── Headline block ─────────────────────── */}
      <header className={`${styles.headline} container-wide`}>
        <div className={styles.headlineLeft}>
          <p className={styles.eyebrow}>{model.provider}</p>
          <h1 className={styles.modelName}>{model.display_name}</h1>
          <p className={styles.modelSlug}>{model.openrouter_id}</p>
        </div>

        <div className={styles.headlineRight}>
          <dl className={styles.statBlock}>
            <div className={styles.statCell}>
              <dt className={styles.statLabel}>Rank</dt>
              <dd className={styles.statValue}>
                {isRanked ? formatRank(model.rank) : <span className={styles.unranked}>tracked</span>}
              </dd>
              {isRanked ? null : (
                <p className={styles.unrankedNote}>insufficient evidence to rank</p>
              )}
            </div>
            <div className={styles.statCell}>
              <dt className={styles.statLabel}>Reception score</dt>
              <dd className={styles.statValueScore}>{formatScore(model.vibe_score)}</dd>
              <p className={styles.statSub}>{formatCI(model.vibe_score_ci)}</p>
            </div>
            <div className={styles.statCell}>
              <dt className={styles.statLabel}>Sample</dt>
              <dd className={styles.statValueSmall}>{formatN(model.sample_size)}</dd>
              <p className={styles.statSub}>judged tweets contributing to the score</p>
            </div>
          </dl>
        </div>
      </header>

      <hr className={`rule container-wide ${styles.headlineRule}`} />

      {/* ─── Summary paragraph ──────────────────── */}
      <section className={`${styles.summary} container-wide`}>
        <p className={styles.summaryEyebrow}>Summary</p>
        <p className={styles.summaryText}>{summary}</p>
      </section>

      {/* ─── Component breakdown ────────────────── */}
      <section className={`${styles.section} container-wide`}>
        <ComponentBreakdown scores={model.components.scores} />
      </section>

      {/* ─── Distributions ──────────────────────── */}
      <section className={`${styles.distributionsGrid} container-wide`} aria-label="Distributions">
        <header className={styles.sectionHeader}>
          <p className={styles.sectionEyebrow}>Distributions</p>
          <h2 className={styles.sectionHeading}>What the judge actually saw.</h2>
        </header>

        <div className={styles.distributionsBody}>
          <SentimentDistributionBar
            distribution={model.stats.distributions.overall_sentiment}
            title="Overall sentiment"
            caption="Tone the judge assigned to each about-family tweet, before any aggregation."
          />
          <SentimentDistributionBar
            distribution={model.stats.distributions.vibe_sentiment}
            title="Vibe sentiment"
            caption="The narrower vibe-only signal — opinions about how it feels to use the family."
          />
          <SourceMix distribution={model.stats.distributions.platform} />
        </div>
      </section>

      {/* ─── Featured discourse ─────────────────── */}
      <section className={`${styles.discourse} container-wide`}>
        <header className={styles.sectionHeader}>
          <p className={styles.sectionEyebrow}>Featured discourse</p>
          <h2 className={styles.sectionHeading}>Representative posts, quoted as written.</h2>
        </header>
        <ol className={styles.discourseList}>
          {model.featured_discourse.map((post) => (
            <li key={post.post_key} className={styles.discourseItem}>
              <div className={styles.discourseMeta}>
                <span className={`${styles.tone} ${styles[`tone_${post.overall_sentiment}`]}`}>
                  {post.overall_sentiment.replace('_', ' ')}
                </span>
                <span className={styles.discourseDivider} aria-hidden="true">·</span>
                <a href={post.url} target="_blank" rel="noreferrer noopener" className={styles.discourseLink}>
                  {post.platform}
                </a>
                <span className={styles.discourseDivider} aria-hidden="true">·</span>
                <span className={styles.discourseDate}>{formatPostDate(post.created_at)}</span>
                <span className={styles.discourseDivider} aria-hidden="true">·</span>
                <span className={styles.discourseConfidence}>conf {post.confidence}</span>
              </div>
              <blockquote className={styles.discourseQuote}>
                <p>&ldquo;{post.text}&rdquo;</p>
              </blockquote>
              {post.rationale_short ? (
                <p className={styles.discourseRationale}>
                  <span className={styles.rationaleLabel}>Judge rationale:</span> {post.rationale_short}
                </p>
              ) : null}
            </li>
          ))}
        </ol>
      </section>

      {/* ─── Methodology footnote ───────────────── */}
      <section className={`${styles.methodologyNote} container-wide`}>
        <p>
          This page is generated automatically from the latest one-week export.
          Scores are computed via {formatLargeInt(meta.bootstrap_iterations)}-iteration bootstrap.
          For the full procedure, see the <Link to="/methodology">methodology page</Link>{' '}
          or the <Link to="/paper">working paper</Link>.
        </p>
      </section>
    </article>
  );
}
